import RelationshipType from "../../models/RelationshipType.mjs";
import Gender from "../../models/Gender.mjs";
import FamilyMember from "../../models/FamilyMember.mjs";
import Self from "../../models/Self.mjs";
export default class BuildTreeTestUtils {
    static _addFather(familyMember, isAlive) {
        const father = new FamilyMember(`${familyMember.name} Father`, RelationshipType.PARENT, Gender.MALE, isAlive);
        familyMember.addFather(father);
        return father;
    }
    static _addMother(familyMember, isAlive) {
        familyMember.addMother(new FamilyMember(`${familyMember.name} Mother`, RelationshipType.PARENT, Gender.FEMALE, isAlive));
    }
    static _getDeceasedMaleSelf() {
        return Self.create("Self", Gender.MALE);
    }
    static _getDeceasedFemaleSelf() {
        return Self.create("Self", Gender.FEMALE);
    }
    static _addNDaughters(familyMember, numberToAdd, numberAlive) {
        this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.CHILD, Gender.FEMALE);
    }
    static _addNSons(familyMember, numberToAdd, numberAlive) {
        return this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.CHILD, Gender.MALE);
    }
    static _addHusband(familyMember, isAlive) {
        familyMember.addSpouse(new FamilyMember(`${familyMember.name} Husband`, RelationshipType.PARENT, Gender.MALE, isAlive));
    }
    static _addNWives(familyMember, numberToAdd, numberAlive) {
        this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.SPOUSE, Gender.FEMALE);
    }
    static _addNGrandSons(familyMember, numberToAdd, numberOfSonsToSplitBetween = 1) {
        this._addNGrandChildren(familyMember, numberToAdd, numberOfSonsToSplitBetween, Gender.MALE);
    }
    static _addNGrandDaughters(familyMember, numberToAdd, numberOfSonsToSplitBetween = 1) {
        this._addNGrandChildren(familyMember, numberToAdd, numberOfSonsToSplitBetween, Gender.FEMALE);
    }
    static _addGrandFather(self, isGrandFatherAlive = true) {
        if (!self._father) {
            throw new Error("Father does not exist - please add father manually first before adding grandfather");
        }
        const father = self._father;
        this._addFather(father, isGrandFatherAlive);
    }
    static _addPaternalGrandMother(self, isGrandMotherAlive = true) {
        if (!self._father) {
            throw new Error("Father does not exist - please add father manually first before adding paternal grandmother");
        }
        const father = self._father;
        this._addMother(father, isGrandMotherAlive);
    }
    static _addMaternalGrandMother(self, isGrandMotherAlive = true) {
        if (!self._mother) {
            throw new Error("Mother does not exist - please add mother manually first before adding maternal grandmother");
        }
        const mother = self._mother;
        this._addMother(mother, isGrandMotherAlive);
    }
    static getFullSiblingTransformation() {
        return {
            apply(self, sibling) {
                sibling.addFather(self._father);
                sibling.addMother(self._mother);
            },
        };
    }
    static getMaternalSiblingTransformation() {
        return {
            apply(self, sibling) {
                sibling.addMother(self._mother);
                if (sibling.gender == Gender.MALE) {
                    self._mother.addSon(sibling);
                }
                else {
                    self._mother.addDaughter(sibling);
                }
            },
        };
    }
    static getPaternalSiblingTransformation() {
        return {
            apply(self, sibling) {
                sibling.addFather(self._father);
                if (sibling.gender == Gender.MALE) {
                    self._father.addSon(sibling);
                }
                else {
                    self._father.addDaughter(sibling);
                }
            },
        };
    }
    static _addNFullSisters(familyMember, numberToAdd, numberAlive) {
        this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.SIBLING, Gender.FEMALE, BuildTreeTestUtils.getFullSiblingTransformation());
    }
    static _addNFullBrothers(familyMember, numberToAdd, numberAlive) {
        this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.SIBLING, Gender.MALE, BuildTreeTestUtils.getFullSiblingTransformation());
    }
    static _addNMaternalSisters(familyMember, numberToAdd, numberAlive) {
        this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.SIBLING, Gender.FEMALE, BuildTreeTestUtils.getMaternalSiblingTransformation());
    }
    static _addNMaternalBrothers(familyMember, numberToAdd, numberAlive) {
        this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.SIBLING, Gender.MALE, BuildTreeTestUtils.getMaternalSiblingTransformation());
    }
    static _addNPaternalSisters(familyMember, numberToAdd, numberAlive) {
        this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.SIBLING, Gender.FEMALE, BuildTreeTestUtils.getPaternalSiblingTransformation());
    }
    static _addNPaternalBrothers(familyMember, numberToAdd, numberAlive) {
        this._addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, RelationshipType.SIBLING, Gender.MALE, BuildTreeTestUtils.getPaternalSiblingTransformation());
    }
    static _addNGrandChildren(familyMember, numberToAdd, numberOfSonsToSplitBetween = 1, gender) {
        const deceasedSons = this._addNSons(familyMember, numberOfSonsToSplitBetween, 0);
        const childrenPerSon = Math.ceil(numberToAdd / numberOfSonsToSplitBetween);
        for (const son of deceasedSons) {
            if (numberToAdd == 0) {
                break;
            }
            const numToAddToSon = Math.min(childrenPerSon, numberToAdd);
            gender === Gender.MALE
                ? this._addNSons(son, numToAddToSon, numToAddToSon)
                : this._addNDaughters(son, numToAddToSon, numToAddToSon);
            numberToAdd -= numToAddToSon;
        }
    }
    static _addNRelationshipsToFamilyMember(familyMember, numberToAdd, numberAlive, relationshipType, gender, transformation) {
        if (relationshipType === RelationshipType.PARENT) {
            throw new Error("Cannot use this method to add parent.");
        }
        if (relationshipType === RelationshipType.SPOUSE &&
            familyMember.gender == Gender.FEMALE) {
            throw new Error("Women can only have a single spouse.");
        }
        const addedMembers = [];
        for (let i = 1; i <= numberToAdd; i++) {
            const memberToAdd = new FamilyMember(
            // Using empty name because for half siblings, we want to determine if "Maternal"/"Paternal" is necessary and we can only check that *after* the transformation is performed. The transformation cannot be performed before the member is created, but in order to create the member, we need to pass in _a_ name, so we can resort for an empty string for now, since it will be overwritten shortly anyway
            "", relationshipType, gender, 
            /* alive = */ i <= numberAlive ? true : false);
            if (transformation) {
                transformation.apply(familyMember, memberToAdd);
            }
            memberToAdd.name = `${familyMember.name} ${this._getSpecificRelationship(relationshipType, gender, familyMember, memberToAdd)} ${i}`;
            switch (relationshipType) {
                case RelationshipType.CHILD:
                    gender == Gender.MALE
                        ? familyMember.addSon(memberToAdd)
                        : familyMember.addDaughter(memberToAdd);
                    break;
                case RelationshipType.SIBLING:
                    gender == Gender.MALE
                        ? familyMember.addBrother(memberToAdd)
                        : familyMember.addSister(memberToAdd);
                    break;
                case RelationshipType.SPOUSE:
                    familyMember.addSpouse(memberToAdd);
                    break;
            }
            addedMembers.push(memberToAdd);
        }
        return addedMembers;
    }
    static _getSpecificRelationshipPrefix(familyMember, inheritor) {
        if ((familyMember === null || familyMember === void 0 ? void 0 : familyMember._father) == (inheritor === null || inheritor === void 0 ? void 0 : inheritor._father) &&
            ((familyMember === null || familyMember === void 0 ? void 0 : familyMember._mother) != (inheritor === null || inheritor === void 0 ? void 0 : inheritor._mother) || !(familyMember === null || familyMember === void 0 ? void 0 : familyMember._mother))) {
            return "Paternal ";
        }
        else if (((familyMember === null || familyMember === void 0 ? void 0 : familyMember._father) != (inheritor === null || inheritor === void 0 ? void 0 : inheritor._father) || !(familyMember === null || familyMember === void 0 ? void 0 : familyMember._father)) &&
            (familyMember === null || familyMember === void 0 ? void 0 : familyMember._mother) == (inheritor === null || inheritor === void 0 ? void 0 : inheritor._mother)) {
            return "Maternal ";
        }
        return "";
    }
    static _getSpecificRelationship(relationshipType, gender, familyMember, inheritor) {
        let specificRelationship = "";
        switch (relationshipType) {
            case RelationshipType.CHILD:
                specificRelationship = gender == Gender.MALE ? "Son" : "Daughter";
                break;
            case RelationshipType.SIBLING:
                specificRelationship =
                    this._getSpecificRelationshipPrefix(familyMember, inheritor) +
                        (gender == Gender.MALE ? "Brother" : "Sister");
                break;
            case RelationshipType.SPOUSE:
                specificRelationship = "Wife";
                break;
        }
        return specificRelationship;
    }
}
